import React, { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import i18n from "micro-i18n";
import { connect } from "unistore/preact";
import ComplianceModal from "components/compliance/ComplianceModal";
import Deck from "components/cards/Deck";
import EditableToolsWrapper from "components/editable/EditableToolsWrapper";
import Error from "components/error/Error";
import { IconMenu } from "components/icons/Icons";
import Loader from "components/loader/Loader";
import MenuModal from "components/menu/MenuModal";
import ReportModal from "components/report/ReportModal";
import ReportModalButton from "components/report/ReportModalButton";
import ShareModal from "components/sharing/ShareModal";
import ShareModalButton from "components/sharing/ShareModalButton";
import HoneyPot from "components/bot/HoneyPot";
import { actions } from "actions";
import { envConfig } from "config";
import { ComplianceState, JotType, SubmitState, Themes, WidgetState, WidgetType } from "consts";
import {
  addWebFonts,
  configureEditableWidget,
  evalNatures,
  getDeckState,
  getUnderModalClass,
  isArray,
  isNullOrUndefOrEmpty,
  onFocusRef,
  toDOMPurifyLazy,
  useBackgroundImage,
  widgetCompliance,
  widgetStyling,
} from "utils";

//FIXME: SOF-617 requires the following imports to be moved to components/widget-components.scss
// due to lazy loading of the widget inside an iframe. Keeping this here for potential fix down the road.
// import 'components/Widget.scss';

const Widget = ({
  bootstrap = {},
  config = {},
  instanceId,
  complianceState,
  submitState,
  widgetState,
  questionsData = {},
  questionIndex = 0,
  widgetInteractable,
  results = [],
  setComplianceState,
  jot,
  frameName,
  theme,
  isFixed,
  darkMode,
}) => {
  if (widgetState === WidgetState.Hidden) {
    //ghost widget
    return null;
  }

  const { id, e: editable } = bootstrap;

  const {
    natures = [],
    delayed,
    partnerName,
    partnerWidgetCss,
    privacyPolicyUrl,
    fontData,
    compliance,
    positionable,
    type: widgetType,
  } = config;
  const targetId = id && id.toString();

  const { isDebugMode } = envConfig;

  // NOTE: @widgetFocusable is a boolean that drives autofocus inside the widget...
  // FALSE (default): the user has not interacted with the widget, there should be NO programmatic focus internally.
  // TRUE: allows for programmatic focus on the Widget internals when the user answers a question or clicks next.
  const [widgetFocusable, setWidgetFocusable] = useState(false);
  const [hasTools, setHasTools] = useState(false);
  //forceHideTools is used during theming so that the admin can see the widget without the editable tools
  const [forceHideTools, setForceHideTools] = useState(false);
  const showTools = editable && hasTools && !forceHideTools;
  const [showComplianceAgreementModal, setShowComplianceAgreementModal] = useState(false);
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [widgetStyleOverrides, setWidgetStyleOverrides] = useState(null);
  const [isWidgetStylingLoaded, setIsWidgetStylingLoaded] = useState(false);
  const [fontsAdded, setFontsAdded] = useState(false);
  const [headerTitle, setHeaderTitle] = useState(null);
  const toggleMenuBtnRef = useRef(null);
  const toggleReportBtnRef = useRef(null);
  const toggleShareBtnRef = useRef(null);
  const widgetHeaderRef = useRef(null);

  // Rules for widget based on widgetState.
  const isLoading = !widgetInteractable || widgetState < WidgetState.Hidden;
  const isLoaded = widgetState > WidgetState.Hidden;
  const hasError = widgetState === WidgetState.Error;
  const canEngage = !hasError && isLoaded && widgetInteractable;
  const {
    backgroundImageEnabled,
    bulkResponseDeleteEnabled,
    complianceFirstEnabled,
    reportingEnabled,
    testShareModalEnabled,
  } = evalNatures(natures);
  const submitTriggered = submitState > SubmitState.Ready;
  const complianceUrl = widgetCompliance.complianceUrl(config);
  const needsComplianceAgreement = canEngage && widgetCompliance.needsComplianceResponse(compliance, complianceState);
  const shareModalEnabled = !needsComplianceAgreement && testShareModalEnabled;
  const underModalClass = getUnderModalClass(
    showReportModal || showComplianceAgreementModal || showMenuModal || showShareModal,
  );
  const isCarousel =
    theme === Themes.CarouselDark || theme === Themes.CarouselLight || widgetType === WidgetType.UserSite;
  const positionableOn = positionable.on === true;
  const showQuestionLoader = widgetState === WidgetState.QuestionsLoading;
  const showQuestions =
    widgetState === WidgetState.Questions && isArray(questionsData.questions) && questionsData.questions.length > 0;
  const showResults = widgetState === WidgetState.Results && isArray(results) && results.length > 0;
  const showFinish = widgetState === WidgetState.Finish;
  const showSummary = widgetState === WidgetState.Summary;
  const { showBackgroundImg, backgroundImageStyles } = useBackgroundImage({
    backgroundImageEnabled,
    questionIndex,
    questionsData,
    widgetState,
  });

  const { introActive, creSlideActive } = getDeckState({
    widgetState,
    questionsData,
    questionIndex,
  });

  const showDeck =
    introActive || creSlideActive || showQuestionLoader || showQuestions || showResults || showFinish || showSummary;

  const showComplianceFirstPage = !isFixed && needsComplianceAgreement && complianceFirstEnabled;

  const showHeaderTitle = !isNullOrUndefOrEmpty(headerTitle) && (!creSlideActive || (creSlideActive && isCarousel));

  // componentDidUpdate specific inputs related to config
  useEffect(() => {
    if (widgetStyling.hasStyleOverrides(partnerWidgetCss)) {
      const { title } = partnerWidgetCss;

      if (!widgetStyleOverrides && !isWidgetStylingLoaded) {
        const styleOverrides = widgetStyling.sanitizedWidgetStyles(partnerWidgetCss);

        if (styleOverrides) {
          toDOMPurifyLazy(styleOverrides, null, true).then((purifiedStyles) => {
            setWidgetStyleOverrides({
              __html: `${purifiedStyles}`,
              styleText: `${purifiedStyles}`,
            });
            return setIsWidgetStylingLoaded(true);
          });
        } else {
          setIsWidgetStylingLoaded(true);
        }
      }

      title &&
        !headerTitle &&
        toDOMPurifyLazy(
          title,
          {
            ALLOWED_TAGS: ["a", "img", "strong", "em", "span"],
            ALLOWED_ATTR: ["href", "target", "src", "alt", "class"],
          },
          true,
        ).then((purifiedTitle) => setHeaderTitle(purifiedTitle ? { __html: `${purifiedTitle}` } : null));
    }

    if (fontData && !fontsAdded) {
      setFontsAdded(true);
      addWebFonts(fontData, frameName);
    }

    if (isLoaded && editable && !hasTools) {
      setHasTools(true);
      configureEditableWidget(instanceId, bootstrap);
    }
  }, [isLoaded, config, bootstrap, hasTools, editable, isWidgetStylingLoaded]);

  // componentDidUpdate specific inputs related to
  // needsComplianceAgreement, submitState, submitTriggered, complianceState
  useEffect(() => {
    // Only show compliance modal if the Respondent had not agreed to the compliance modal
    // AND the first question is in a submittable state.
    const showCompliance = needsComplianceAgreement && submitTriggered;
    setShowComplianceAgreementModal(!showComplianceFirstPage && showCompliance);
    showCompliance && !showComplianceFirstPage && jot({ space: "poll", type: JotType.ComplianceShown });
    showComplianceFirstPage && submitTriggered && jot({ space: "poll", type: JotType.ComplianceShownFirst });
  }, [needsComplianceAgreement, submitState, submitTriggered, complianceState, showComplianceFirstPage]);

  //Adding widget overrides as textContent
  const widgetStyleOverridesRef = useCallback(
    (node) => {
      if (node && widgetStyleOverrides && widgetStyleOverrides.styleText) {
        node.textContent = widgetStyleOverrides.styleText;
      }
    },
    [widgetStyleOverrides],
  );

  const onWidgetFocus = () => {
    !widgetFocusable && setWidgetFocusable(true);
  };

  /**
   * @param {boolean | null} agree - true=accept, false=decline, null=no-change (delete-only)
   * @param {boolean} [deleteData] - if true, request server-side data deletion
   */
  const onComplianceAgree = (agree, deleteData = false) => {
    const complianceCheckOn = widgetCompliance.complianceCheckOn(compliance);
    const complianceVersion = complianceCheckOn ? compliance["version"] : widgetCompliance.DefaultComplianceVersion;
    const complianceState = agree === null ? null : agree ? ComplianceState.Accepted : ComplianceState.NotAccepted;
    return setComplianceState({
      complianceState,
      complianceVersion,
      saveComplianceAnswer: complianceCheckOn,
      deleteData,
    });
  };

  const onNextButtonCompliance = showComplianceFirstPage ? () => onComplianceAgree(true) : null;

  const toggleMenuModal = () => {
    !showMenuModal && jot({ space: "poll", type: JotType.MenuOpen });
    setShowMenuModal(!showMenuModal);
  };

  const hideMenuModal = () => {
    setShowMenuModal(false);
    widgetFocusable && onFocusRef(toggleMenuBtnRef);
  };
  const onToggleReportModal = () => setShowReportModal(!showReportModal);

  const onCloseReportModal = () => {
    setShowReportModal(false);
    widgetFocusable && onFocusRef(toggleReportBtnRef);
  };

  const onToggleShareModal = () => setShowShareModal(!showShareModal);

  const onCloseShareModal = () => {
    setShowShareModal(false);
    widgetFocusable && onFocusRef(toggleShareBtnRef);
  };

  const renderWidgetTemplate = () => {
    const widgetTemplate = [];

    //NOTE: Render of header and questions below need to take place AFTER the widget styling has
    // been async loaded and processed.
    isWidgetStylingLoaded &&
      widgetTemplate.push(
        <div
          key={`csw-${instanceId}`}
          onClick={onWidgetFocus}
          onKeyUp={onWidgetFocus}
          className={classNames("csw", {
            "csw-type-carousel": isCarousel,
            "csw-dark-mode": darkMode,
            "csw-no-header-with-extra-icons": (reportingEnabled || shareModalEnabled) && !showHeaderTitle,
            "csw-with-bg-img": showBackgroundImg,
          })}
          style={backgroundImageStyles}
        >
          <HoneyPot />

          {widgetStyleOverrides && <style id="csw-style-overrides" ref={widgetStyleOverridesRef} />}

          {(isLoaded || delayed) && (
            <header
              ref={widgetHeaderRef}
              tabIndex="0"
              aria-labelledby={`csw-header-txt-${id}`}
              className={classNames("csw-header", underModalClass, {
                "csw-header-title": showHeaderTitle,
                "csw-header-no-title": !showHeaderTitle,
                "csw-header-loaded": isLoaded,
              })}
            >
              <div className="csw-header-txt" id={`csw-header-txt-${id}`}>
                {showHeaderTitle && <span dangerouslySetInnerHTML={headerTitle} />}
              </div>

              {!isLoading && (
                <>
                  {showQuestions && reportingEnabled && (
                    <ReportModalButton
                      onToggleModal={onToggleReportModal}
                      showModal={showReportModal}
                      modalId={`csw-report-modal-${id}`}
                      className="csw-header-btn"
                      focusRef={toggleReportBtnRef}
                    />
                  )}

                  {showQuestions && shareModalEnabled && (
                    <ShareModalButton
                      onToggleModal={onToggleShareModal}
                      showModal={showReportModal}
                      modalId={`csw-share-modal-${targetId}`}
                      className="csw-header-btn"
                      focusRef={toggleShareBtnRef}
                    />
                  )}

                  <button
                    type="button"
                    className="csw-menu-btn csw-btn csw-btn-icon outline"
                    ref={toggleMenuBtnRef}
                    onClick={toggleMenuModal}
                    aria-expanded={showMenuModal}
                    aria-haspopup="true"
                    aria-controls={`csw-menu-modal-${targetId}`}
                    aria-label={i18n.t("MENU_OPEN")}
                    title={i18n.t("MENU_OPEN")}
                  >
                    <IconMenu />
                    <span className="sr-only">{i18n.t("MENU_OPEN")}</span>
                  </button>
                </>
              )}
            </header>
          )}

          <div
            role="main"
            aria-label={i18n.t("SURVEY_MAIN")}
            className={classNames("csw-data-scroll", underModalClass, {
              "csw-fix-scroll": isFixed,
            })}
          >
            {isLoading && (
              <Loader
                className="csw-config-loading"
                loaderMessage={delayed ? i18n.t("LOADING_SURVEY_DELAYED") : i18n.t("LOADING_SURVEY")}
                isAnimated={!delayed}
              />
            )}

            {isDebugMode && hasError && <Error />}

            {canEngage && (
              <div className="csw-data">
                {showDeck && (
                  <Deck
                    focusable={widgetFocusable}
                    isCarousel={isCarousel}
                    isFixed={isFixed}
                    complianceUrl={complianceUrl}
                    onNextButtonCompliance={onNextButtonCompliance}
                    onToggleReportModal={onToggleReportModal}
                  />
                )}
              </div>
            )}
          </div>

          {canEngage && (
            <MenuModal
              id={`csw-menu-modal-${targetId}`}
              targetId={targetId}
              complianceUrl={complianceUrl}
              partnerName={partnerName}
              privacyPolicyUrl={privacyPolicyUrl}
              showModal={showMenuModal}
              onClose={hideMenuModal}
              closeFocusRef={widgetHeaderRef}
              theme={theme}
              complianceState={complianceState}
              compliance={compliance}
              onComplianceAgree={onComplianceAgree}
              focusable={widgetFocusable}
              bulkResponseDeleteEnabled={bulkResponseDeleteEnabled}
            />
          )}

          {shareModalEnabled && (
            <ShareModal
              id={`csw-share-modal-${targetId}`}
              showModal={showShareModal}
              onClose={onCloseShareModal}
              closeFocusRef={widgetHeaderRef}
              theme={theme}
              focusable={widgetFocusable}
            />
          )}

          {needsComplianceAgreement && (
            <ComplianceModal
              id={`csw-compliance-modal-${id}`}
              onComplianceAgree={onComplianceAgree}
              complianceUrl={complianceUrl || ""}
              showModal={showComplianceAgreementModal}
              closeFocusRef={widgetHeaderRef}
              theme={theme}
              focusable={widgetFocusable}
              jot={jot}
            />
          )}

          {reportingEnabled && (
            <ReportModal
              id={`csw-report-modal-${id}`}
              showModal={showReportModal}
              onClose={onCloseReportModal}
              closeFocusRef={widgetHeaderRef}
              theme={theme}
              focusable={widgetFocusable}
            />
          )}

          <div
            className={classNames(underModalClass, {
              "csw-editable-tools-wrapper": showTools,
              hide: forceHideTools,
            })}
          >
            {showTools && (
              <EditableToolsWrapper
                positionable={positionable}
                showTools={showTools}
                positionableOn={positionableOn}
                onHideTools={() => setForceHideTools(true)}
              />
            )}
          </div>
          {showTools && <div className="csw-editable-tools-spacer" />}
        </div>,
      );

    return <>{widgetTemplate}</>;
  };

  return renderWidgetTemplate();
};

Widget.propTypes /* remove-proptypes */ = {
  bootstrap: PropTypes.object,
  config: PropTypes.object,
  instanceId: PropTypes.string,
  complianceState: PropTypes.string,
  submitState: PropTypes.number,
  widgetState: PropTypes.number,
  questionsData: PropTypes.object,
  questionIndex: PropTypes.number,
  widgetInteractable: PropTypes.bool,
  results: PropTypes.array,
  setComplianceState: PropTypes.func,
  jot: PropTypes.func,
  frameName: PropTypes.string,
  theme: PropTypes.string,
  isFixed: PropTypes.bool,
  darkMode: PropTypes.bool,
};

export default connect(
  `bootstrap,config,instanceId,complianceState,submitState,widgetState,
  questionsData,questionIndex,widgetInteractable,results,darkMode`,
  actions,
)(Widget);
